/**
 * DefaultSEO — Oct 31 2026 · SEO audit.
 * Only 13 of ~220 pages render <SEO>; the rest inherited index.html's static
 * canonical that pointed EVERY page at the homepage. This fallback renders a
 * self-referencing canonical + og:url + hreflang pair for the current route.
 * React 19 + react-helmet-async render head tags natively (no dedupe), so:
 *   • pages that render <SEO> register as owner via PageSeoContext and this
 *     fallback steps aside (no duplicate canonicals);
 *   • the static index.html tags marked data-static-seo are removed once the
 *     app mounts (crawlers still see the baked per-route values in raw HTML).
 * It sets NO title/description, so pages that set document.title are untouched.
 */
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SEO_ROUTES } from "@/data/seoRoutes";
import { treatmentJsonLd } from "@/data/treatmentSchema";

const SITE_URL = "https://karinelauzon.com";

export const PageSeoContext = createContext({ owned: false, setOwned: () => {} });

export function PageSeoProvider({ children }) {
  const [owned, setOwned] = useState(false);
  return <PageSeoContext.Provider value={{ owned, setOwned }}>{children}</PageSeoContext.Provider>;
}

// Call from any component that renders its own canonical (SEO.jsx does).
export function usePageSeoOwner() {
  const { setOwned } = useContext(PageSeoContext);
  useEffect(() => { setOwned(true); return () => setOwned(false); }, [setOwned]);
}

// FR routes that mirror an EN route 1:1 (`/fr` + EN path). Mirrors App.js.
export const FR_MIRROR_PATHS = new Set([
  "/botox-ottawa", "/dermal-filler-ottawa", "/karine-lauzon-rn", "/locations/ottawa", "/locations/kanata",
  "/locations/carleton-place", "/radiesse-hands", "/radiesse-neck-decollete", "/sculptra-ottawa", "/skincare-101",
]);

const NOINDEX_PREFIXES = ["/admin", "/staff", "/portal", "/kiosk", "/signage", "/lobby", "/head-office", "/md", "/my-account", "/preview", "/checkin", "/pay"];

export function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

export default function DefaultSEO() {
  const { pathname } = useLocation();
  const { owned } = useContext(PageSeoContext);

  useEffect(() => {
    // Drop index.html's static SEO tags once React owns the head. The static
    // <title> is kept only while no React-rendered <title> exists (homepage).
    const sweep = () => {
      document.querySelectorAll("head [data-static-seo]:not(title)").forEach((el) => el.remove());
      const titles = document.querySelectorAll("head title");
      if (titles.length > 1) document.querySelectorAll("head title[data-static-seo]").forEach((el) => el.remove());
    };
    sweep();
    const t = setTimeout(sweep, 1500);
    return () => clearTimeout(t);
  }, [pathname]);

  const path = normalizePath(pathname);
  const isFr = path === "/fr" || path.startsWith("/fr/");
  const enPath = isFr ? (path.replace(/^\/fr/, "") || "/") : path;
  const hasFr = FR_MIRROR_PATHS.has(enPath);
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;
  const noindex = NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
  const reg = SEO_ROUTES[path] || null;
  const procedure = treatmentJsonLd(isFr ? enPath : path);

  return (
    <Helmet>
      <html lang={isFr ? "fr-CA" : "en-CA"} />
      {(!owned || noindex) && <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />}
      {procedure && <script type="application/ld+json">{JSON.stringify(procedure)}</script>}
      {!owned && reg?.title && <title>{reg.title}</title>}
      {!owned && reg?.description && <meta name="description" content={reg.description} />}
      {!owned && reg?.title && <meta property="og:title" content={reg.title} />}
      {!owned && reg?.description && <meta property="og:description" content={reg.description} />}
      {!owned && <link rel="canonical" href={canonical} />}
      {!owned && <meta property="og:url" content={canonical} />}
      {!owned && hasFr && <link rel="alternate" hrefLang="en-CA" href={`${SITE_URL}${enPath === "/" ? "/" : enPath}`} />}
      {!owned && hasFr && <link rel="alternate" hrefLang="fr-CA" href={`${SITE_URL}/fr${enPath === "/" ? "" : enPath}`} />}
      {!owned && hasFr && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${enPath === "/" ? "/" : enPath}`} />}
    </Helmet>
  );
}
